import { useEffect, useState } from "react";
import {
  deleteClientViewVendorSearchSource,
  previewClientViewVendorSearchSourceDeletion,
} from "../../api/clientSettingsApi";

function productionBadgeClass(code) {
  if (code === "blocked") return "cvd-badge--suspended";
  if (code === "production" || code === "production_internal" || code === "production_search") {
    return "cvd-badge--published";
  }
  return "cvd-badge--warning";
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function ImpactGroup({ title, lines, empty }) {
  return (
    <section className="cp-settings-manage-source__impact">
      <h4>{title}</h4>
      {lines?.length ? (
        <ul>
          {lines.map((line) => (
            <li key={`${line.action}-${line.label}`}>
              <strong>{formatCount(line.count)}</strong> {line.label}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{empty}</p>
      )}
    </section>
  );
}

export default function ManageVendorSearchSourceModal({
  clientViewId,
  source,
  onClose,
  onDeleted,
}) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [error, setError] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const management = source?.management || {};
  const sourceName = source?.name || "this source";

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    setError("");
    setPreview(null);
    setConfirmName("");
    setAcknowledged(false);

    previewClientViewVendorSearchSourceDeletion(clientViewId, source.id)
      .then((body) => {
        if (!cancelled) setPreview(body.preview || body);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || "Unable to check what deleting this source would change.");
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientViewId, source.id]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !deleting) onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [deleting, onClose]);

  const nameMatches = confirmName.trim() === sourceName;
  const canDelete = Boolean(preview?.deletable) && acknowledged && nameMatches && !deleting;

  const handleDelete = async () => {
    if (!canDelete) return;
    try {
      setDeleting(true);
      setError("");
      await deleteClientViewVendorSearchSource(clientViewId, source.id);
      onDeleted?.(source);
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete this data source.");
      setDeleting(false);
    }
  };

  return (
    <div
      className="client-vendor-modal-backdrop"
      role="presentation"
      onClick={() => !deleting && onClose?.()}
    >
      <div
        className="client-vendor-modal cp-settings-manage-source"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-vendor-source-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="client-vendor-modal-header">
          <div>
            <p className="cp-settings-manage-source__eyebrow">Manage vendor search source</p>
            <h3 id="manage-vendor-source-title">{sourceName}</h3>
          </div>
          <button type="button" className="cv-button-link" onClick={onClose} disabled={deleting}>
            Close
          </button>
        </div>

        <div className="cp-settings-manage-source__body">
          {error ? <p className="admin-data-health-error">{error}</p> : null}

          <section className="cp-settings-manage-source__section">
            <h4>What this source does</h4>
            <p>{management.useSummary || source.syncDescription || "Syncs vendor records for CasaMapa search."}</p>
            {management.useDetail ? <p className="muted">{management.useDetail}</p> : null}
            {management.owner ? <p className="muted">Owner: {management.owner}</p> : null}
          </section>

          <section className="cp-settings-manage-source__section">
            <h4>Production use</h4>
            <p>
              <span className={`cvd-badge ${productionBadgeClass(management.productionUse)}`}>
                {management.productionTitle || "Production use not documented"}
              </span>
            </p>
            <p>
              {management.productionExplanation ||
                "Review this source before using it for public vendor listings."}
            </p>
          </section>

          <section className="cp-settings-manage-source__section">
            <h4>Impacts</h4>
            <ul className="cp-settings-manage-source__impacts">
              {(management.impacts || []).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>

          <section className="cp-settings-manage-source__section cp-settings-manage-source__section--danger">
            <h4>Delete this source</h4>
            <p>
              Deleting <strong>{sourceName}</strong> removes it from Vendor Search. This cannot be
              undone.
            </p>
            {loadingPreview ? <p className="muted">Checking what this delete would change…</p> : null}
            {!loadingPreview && preview?.blockedReason ? <p>{preview.blockedReason}</p> : null}
            {!loadingPreview && preview ? (
              <>
                <ImpactGroup
                  title="Removed with this source"
                  lines={preview.removed}
                  empty="Only the source record itself."
                />
                <ImpactGroup
                  title="Unlinked, not deleted"
                  lines={preview.unlinked}
                  empty="No review records need to be unlinked."
                />
                <ImpactGroup
                  title="Left in place"
                  lines={preview.retained}
                  empty="No accepted vendors stay behind."
                />
                {preview.notes?.length ? (
                  <ul className="cp-settings-manage-source__notes">
                    {preview.notes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}

            {preview?.deletable ? (
              <>
                <label className="cp-settings-manage-source__confirm">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                    disabled={deleting}
                  />
                  <span>I understand this cannot be undone.</span>
                </label>
                <label className="admin-users-field">
                  <span>Type {sourceName} to confirm</span>
                  <input
                    type="text"
                    value={confirmName}
                    onChange={(event) => setConfirmName(event.target.value)}
                    disabled={deleting}
                    autoComplete="off"
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={!canDelete}
                  onClick={handleDelete}
                >
                  {deleting ? "Deleting…" : "Delete data source"}
                </button>
              </>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}
